import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import { getAgCihazlariPage, STRAPI_URL } from '../services/api';
import DynamicIcon from '../components/DynamicIcon';
import DEFAULT_DATA from '../default_datas/pages/defaultAgCihazlari';

const resolveMediaUrl = (mediaField) => {
  if (!mediaField) return null;
  const media = Array.isArray(mediaField) ? mediaField[0] : mediaField;
  const url = media?.url;
  if (!url) return null;
  return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
};

const AgCihazlari = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tx = (tr, en) => (lang === 'en' ? (en || tr) : tr);

  useEffect(() => {
    getAgCihazlariPage()
      .then((result) => {
        setData(result || null);
      })
      .catch((err) => {
        console.error('Strapi fetch hatası:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#2a9d8f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const sections = data?.pageContent || [];
  const heroRaw = sections.find((s) => s.__component === 'sections.hero');
  const statsRaw = sections.find((s) => s.__component === 'sections.stats-media-section');
  const featuresRaw = sections.find((s) => s.__component === 'sections.features-section');
  const deviceTypesRaw = sections.find((s) => s.__component === 'sections.device-types-section');
  const ctaRaw = sections.find((s) => s.__component === 'sections.cta');

  const hero = { ...DEFAULT_DATA.hero, ...(heroRaw || {}) };
  const statsSection = statsRaw?.stats?.length > 0 ? statsRaw : DEFAULT_DATA.statsSection;
  const features = featuresRaw ? { ...DEFAULT_DATA.features, ...featuresRaw } : DEFAULT_DATA.features;
  const deviceTypes = deviceTypesRaw ? { ...DEFAULT_DATA.deviceTypes, ...deviceTypesRaw } : DEFAULT_DATA.deviceTypes;
  const cta = ctaRaw ? { ...DEFAULT_DATA.cta, ...ctaRaw } : DEFAULT_DATA.cta;

  const heroImageUrl = resolveMediaUrl(hero.image);

  return (
    <>
      <Seo
        title={lang === 'en' ? 'Network Infrastructure Security | RUZY' : 'Ağ Altyapısı Güvenliği | RUZY'}
        description={lang === 'en' ? 'Centrally manage all your network assets.' : 'Tüm ağ varlıklarınızı merkezi olarak yönetin.'}
        path="/ag-cihazlari"
      />

      <div className="font-poppins text-[#0D3A2F] overflow-x-hidden">
        <ScrollReveal>
          <section className="flex flex-col lg:flex-row items-center justify-between px-6 lg:px-28 py-10 lg:py-20 bg-[#f5faf9] gap-10 lg:gap-16">
            <div className="flex-1 max-w-full lg:max-w-[520px] text-center lg:text-left">
              {hero.badge && (
                <span className="inline-block bg-[#e0f2ef] text-[#2a9d8f] text-[10px] lg:text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide">
                  {tx(hero.badge, hero.badge_en)}
                </span>
              )}

              <h1 className="text-3xl lg:text-[42px] font-bold leading-tight mb-5">
                {tx(hero.title, hero.title_en)}
              </h1>

              <p className="text-sm lg:text-[15px] leading-relaxed text-[#4a6b63] mb-8">
                {tx(hero.description, hero.description_en)}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() => navigate('/iletisim')}
                  className="h-12 lg:h-auto bg-[#2a9d8f] text-white border-none px-7 py-3.5 rounded-xl text-sm lg:text-[15px] font-semibold cursor-pointer"
                >
                  {tx(hero.primaryButtonText, hero.primaryButtonText_en)}
                </button>
              </div>
            </div>

            <div className="flex-1 flex justify-center w-full">
              <div className="relative w-full max-w-[460px]">
                {heroImageUrl ? (
                  <img
                    src={heroImageUrl}
                    alt={tx(hero.title, hero.title_en)}
                    className="w-full h-[250px] lg:h-auto rounded-2xl object-cover shadow-2xl"
                  />
                ) : (
                  <div className="w-full h-64 bg-[#e0f2ef] rounded-2xl flex items-center justify-center text-[#2a9d8f]">
                    {lang === 'en' ? 'Loading...' : 'Yükleniyor...'}
                  </div>
                )}

                {statsSection?.stats?.[0] && (
                  <div className="absolute bottom-4 left-4 bg-white rounded-xl px-4 py-3 shadow-xl flex items-center gap-3 border border-[#f0f0f0] w-fit">
                    <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-[#e0f2ef] flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-[#2a9d8f] text-base lg:text-lg">verified</span>
                    </div>
                    <div>
                      <div className="text-base lg:text-xl font-bold text-[#0D3A2F] leading-tight">
                        {tx(statsSection.stats[0].value, statsSection.stats[0].value_en)}
                        {statsSection.stats[0].small && (
                          <small className="text-xs font-normal ml-1" style={{ color: statsSection.stats[0].smallRenk || '#2a9d8f' }}>
                            {tx(statsSection.stats[0].small, statsSection.stats[0].small_en)}
                          </small>
                        )}
                      </div>
                      <div className="text-[9px] lg:text-[10px] font-bold text-[#6b9e96] uppercase tracking-widest">
                        {tx(statsSection.stats[0].label, statsSection.stats[0].label_en)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </ScrollReveal>

        {features && (
          <ScrollReveal>
            <section className="px-6 lg:px-28 py-12 lg:py-20 bg-white text-center">
              <h2 className="text-2xl lg:text-4xl font-bold text-[#0D3A2F] mb-3 leading-tight">
                {tx(features.Title, features.Title_en)}
              </h2>
              <p className="text-sm lg:text-[15px] text-[#4a6b63] max-w-[500px] mx-auto mb-10 lg:mb-12 leading-relaxed">
                {tx(features.Description, features.Description_en)}
              </p>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {(features.cards || []).map((f, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 lg:px-5 lg:py-7 text-left border border-[#e0eeeb] flex flex-col gap-3">
                    <div className="text-[#2a9d8f] scale-90 lg:scale-100 origin-left">
                      <DynamicIcon name={f.icon || 'FaShieldAlt'} size={26} />
                    </div>
                    <h3 className="text-xs lg:text-base font-semibold text-[#0D3A2F] leading-snug">
                      {tx(f.title || f.Title, f.title_en || f.Title_en)}
                    </h3>
                    <div className="text-sm leading-relaxed text-[#4a6b63] flex flex-col gap-1.5">
                      {tx(f.description || f.Description, f.description_en || f.Description_en)
                        ?.split('\n')
                        .map((line, j) =>
                          line.startsWith('•') ? (
                            <span key={j} className="flex items-center gap-2 text-[#0D3A2F] font-semibold text-xs">
                              <span className="material-symbols-outlined text-[#2a9d8f] text-base">check_circle</span>
                              {line.replace('•', '').trim()}
                            </span>
                          ) : (
                            <span key={j}>{line}</span>
                          )
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </ScrollReveal>
        )}

        {deviceTypes && (
          <ScrollReveal>
            <section className="px-6 lg:px-28 py-12 lg:py-20 bg-[#f5faf9]">
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0D3A2F] mb-8 text-center lg:text-left">
                {tx(deviceTypes.title, deviceTypes.title_en)}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                {(deviceTypes.cards || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-4 border border-[#e0eeeb] rounded-xl p-4 lg:p-5">
                    <div className="text-[#2a9d8f] flex-shrink-0">
                      <DynamicIcon name={c.icon || 'FaNetworkWired'} size={28} />
                    </div>
                    <div>
                      <h3 className="text-sm lg:text-[15px] font-semibold text-[#0D3A2F] mb-0.5">
                        {tx(c.label, c.label_en)}
                      </h3>
                      <p className="text-[11px] lg:text-sm text-[#4a6b63] leading-tight">
                        {tx(c.description, c.description_en)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </ScrollReveal>
        )}

        {cta && (
          <section className="bg-white py-10 lg:py-20 px-6">
            <div className="max-w-[1280px] mx-auto bg-[#2a9d8f] rounded-2xl lg:rounded-3xl p-6 lg:p-16 text-center lg:text-left flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-10">
              <div className="lg:w-2/3">
                <h2 className="text-xl lg:text-4xl font-bold text-white mb-2 leading-tight">
                  {tx(cta.Title, cta.Title_en)}
                </h2>
                <p className="text-[11px] lg:text-[15px] text-white/85">
                  {tx(cta.Description, cta.Description_en)}
                </p>
              </div>
              <button
                onClick={() => navigate('/iletisim')}
                className="h-12 lg:h-14 bg-white text-[#2a9d8f] font-bold px-8 rounded-xl text-sm lg:text-[15px] cursor-pointer hover:bg-[#f0faf9] transition-all border-none w-full lg:w-auto"
              >
                {tx(cta.primaryButtonText, cta.primaryButtonText_en)}
              </button>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default AgCihazlari;